import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AppConfigService } from './app-config.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LocalLanguageService {

  private readonly LANG_KEY = 'lang';

  constructor(private translateService: TranslateService,
              private appConfigService: AppConfigService) {
  }

  setUp() {
    const lang: string = this.getLanguage();
    this.translateService.use(lang);
    this.translateService.setFallbackLang(lang);
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      const eventLang: string = event.lang;
      if (eventLang && eventLang !== 'undefined') {
        window.localStorage.setItem(this.LANG_KEY, eventLang);
      } else {
        window.localStorage.setItem(this.LANG_KEY, lang);
      }
    });
    this.appConfigService.kanton$.subscribe(kanton => {
      this.configureLanguages(kanton);
      if (environment.deploymentMode === 'PUBLIC') {
        this.overwriteWithPublic(kanton);
      }
    });
  }

  getLocalLanguage(): string {
    const lang = localStorage.getItem(this.LANG_KEY);
    if (lang) {
      return lang;
    }
    return this.translateService.getBrowserLang();
  }

  private getLanguage() {
    const language = this.getLocalLanguage();
    const isLanguageSupported = this.getSupportedLanguages().has(language);
    if (isLanguageSupported && language !== 'undefined') {
      return language;
    }
    return 'de';
  }

  private configureLanguages(kanton: string) {
    this.configureBasicLanguages();
    this.getKantonalLanguages().forEach((map: Map<string, any>, key: string) => {
      if (key === kanton) {
        map.forEach((value: any, k: string) =>
          this.translateService.setTranslation(k, value, true));
      }
    });
  }

  private overwriteWithPublic(kanton: string) {
    this.getSupportedLanguagesForPublic().forEach((value: any, key: string) => {
      this.translateService.setTranslation(key, value, true);
    });
    this.getKantonalLanguagesForPublic().forEach((map: Map<string, any>, key: string) => {
      if (key === kanton) {
        map.forEach((value: any, k: string) =>
          this.translateService.setTranslation(k, value, true));
      }
    });
  }

  private getSupportedLanguages(): Map<string, string> {
    const languages = new Map<string, any>();
    languages.set('de', require('../../../i18n/locale-de.json'));
    languages.set('fr', require('../../../i18n/locale-fr.json'));
    languages.set('it', require('../../../i18n/locale-it.json'));
    return languages;
  }

  private getSupportedLanguagesForPublic(): Map<string, string> {
    const languages = new Map<string, any>();
    languages.set('de', require('../../../i18n/locale-de-public.json'));
    languages.set('fr', require('../../../i18n/locale-fr-public.json'));
    return languages;
  }

  private getKantonalLanguagesForPublic(): Map<string, Map<string, string>> {
    const languages = new Map<string, Map<string, any>>();
    const bemap = new Map<string, any>()
      .set('de', require('../../../i18n/locale-de-be-public.json'))
      .set('fr', require('../../../i18n/locale-fr-be-public.json'));
    languages.set('BE', bemap);
    return languages;
  }

  private getKantonalLanguages(): Map<string, Map<string, string>> {
    const languages = new Map<string, Map<string, any>>();
    languages.set('AG', new Map<string, any>().set('de', require('../../../i18n/locale-de-ag.json')));
    languages.set('GE', new Map<string, any>().set('fr', require('../../../i18n/locale-fr-ge.json')));
    languages.set('VD', new Map<string, any>().set('fr', require('../../../i18n/locale-fr-vd.json')));
    languages.set('GR', new Map<string, any>().set('de', require('../../../i18n/locale-de-gr.json')));
    const bemap = new Map<string, any>()
      .set('de', require('../../../i18n/locale-de-be.json'))
      .set('fr', require('../../../i18n/locale-fr-be.json'));
    languages.set('BE', bemap);
    const bimap = new Map<string, any>()
      .set('de', require('../../../i18n/locale-de-bi.json'))
      .set('fr', require('../../../i18n/locale-fr-bi.json'));
    languages.set('BI', bimap);
    const bsmap = new Map<string, any>()
      .set('de', require('../../../i18n/locale-de-bs.json'))
      .set('fr', require('../../../i18n/locale-fr-bs.json'));
    languages.set('BS', bsmap);

    return languages;
  }

  private configureBasicLanguages() {
    this.getSupportedLanguages().forEach((value: any, key: string) => {
      this.translateService.setTranslation(key, value);
    });
  }
}
